import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/lib/api'
import type { Convention, Identity, Meta, Role } from '@/lib/types'

export const useSession = defineStore('session', () => {
  const identity = ref<Identity | null>(null)
  const convention = ref<Convention | null>(null)
  const meta = ref<Meta | null>(null)
  const error = ref<string | null>(null)
  const loading = ref(false)

  async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const [me, m] = await Promise.all([
        api<{ identity: Identity; convention: Convention | null }>('/api/me'),
        api<Meta>('/api/meta'),
      ])
      identity.value = me.identity
      convention.value = me.convention
      meta.value = m
    } catch (e) {
      error.value = (e as Error).message
      identity.value = null
    } finally {
      loading.value = false
    }
  }

  function clear(): void {
    identity.value = null
    convention.value = null
  }

  /** Mirrors the API's rule: admin passes every role check. */
  function hasRole(...roles: Role[]): boolean {
    const mine = identity.value?.roles ?? []
    return mine.includes('admin') || roles.some((r) => mine.includes(r))
  }

  const stubs = computed(() => {
    const m = meta.value
    if (!m) return []
    return [
      m.authMode === 'dev' ? 'sign-in (dev impersonation)' : null,
      m.cventMode === 'stub' ? 'Cvent' : null,
      m.printMode === 'stub' ? 'badge printing' : null,
      m.notifyMode === 'stub' ? 'email' : null,
    ].filter((x): x is string => x != null)
  })

  return { identity, convention, meta, error, loading, load, clear, hasRole, stubs }
})
